import { useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { api } from '@/services/api';
import { toast } from '@/hooks/use-toast';
import { withDimensions } from '@/lib/assumption-risk';

export function AddAssumptionButton({
  projectId,
  ideaNodeId,
  disabled,
  readOnly,
}: {
  projectId: string;
  ideaNodeId?: string;
  disabled?: boolean;
  // True for viewers, who are disabled for a different reason than a missing
  // idea. Kept separate so the tooltip can tell them the true cause instead
  // of the idea-capture message, which would be false for them.
  readOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (!open) {
    // Gated by the parent (mirrors the generate gating): an idea must be
    // captured — both so there's something to assume about and so there's an
    // idea node to link to, otherwise a manual assumption would land
    // disconnected. (ideaNodeId can exist with empty idea text under the
    // blank-not-delete lifecycle, hence gating on project.idea too.)
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={readOnly ? "Viewers can't add assumptions" : disabled ? 'Capture your idea in step 1 first' : undefined}
      >
        <Plus className="h-3.5 w-3.5 mr-1.5" />
        Add assumption manually
      </Button>
    );
  }
  return <AddAssumptionForm projectId={projectId} ideaNodeId={ideaNodeId} onDone={() => setOpen(false)} />;
}

export function AddAssumptionForm({
  projectId,
  ideaNodeId,
  onDone,
}: {
  projectId: string;
  ideaNodeId?: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const t = title.trim();
    if (!t) {
      toast({ title: 'Title required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const nodeResp = await api.createNode({
        projectId,
        type: 'assumption',
        title: t,
        description: description.trim() || undefined,
        metadata: withDimensions({ source: 'manual' }, 'low', 'unknown'),
      });
      if (!nodeResp.success || !nodeResp.data) throw new Error(nodeResp.message || 'Failed to create');
      const newId = (nodeResp.data as { id: string }).id;

      // Link to the idea node so a manual assumption isn't orphaned off the
      // graph (generation does the same fallback server-side). The node is the
      // valuable part — if the edge write is rejected (createEdge resolves
      // { success: false } on HTTP errors, and may throw on network ones) we
      // keep the node but warn, rather than silently leaving it unconnected
      // while the UI implies it was linked.
      let linked = true;
      if (ideaNodeId) {
        const edgeResp = await api
          .createEdge({ projectId, sourceNodeId: newId, targetNodeId: ideaNodeId, edgeType: 'assumption-node' })
          .catch(() => ({ success: false as const }));
        linked = edgeResp.success;
      }

      await queryClient.invalidateQueries({ queryKey: ['canvas', projectId] });
      onDone();
      if (!linked) {
        toast({
          title: 'Assumption added, but not linked',
          description: "Couldn't connect it to your idea, so it may show as unconnected on the graph.",
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({ title: 'Save failed', description: err instanceof Error ? err.message : '', variant: 'destructive' });
      setSaving(false);
    }
  };

  return (
    <div className="w-full space-y-3 rounded-md border bg-card p-3">
      <label className="block space-y-1">
        <span className="text-sm font-medium">Assumption</span>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="What must be true for the idea to work?"
          maxLength={500}
          className="w-full px-3 py-2 border rounded-md bg-background text-sm focus:outline-hidden focus:ring-2 focus:ring-ring"
        />
      </label>
      <label className="block space-y-1">
        <span className="text-sm font-medium">Why / context</span>
        <span className="text-xs text-muted-foreground ml-2">optional</span>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          maxLength={2000}
          className="w-full px-3 py-2 border rounded-md bg-background text-sm focus:outline-hidden focus:ring-2 focus:ring-ring resize-none"
        />
      </label>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={handleSave} disabled={saving || !title.trim()}>
          {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
          Save
        </Button>
        <Button variant="outline" size="sm" onClick={onDone} disabled={saving}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
